import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { clearTrainingLoginAlertFlags } from '../utils/trainingLoginAlert';

/**
 * 모듈 최초 평가 시점의 브라우저 fetch.
 * useEffect 안에서 window.fetch.bind(window)를 쓰면, 이미 래핑된 fetch를 잡아
 * await fetch → 래퍼 → 래퍼 … 무한 대기(로딩만 계속)가 날 수 있음.
 */
const MRT_NATIVE_FETCH =
  typeof window !== 'undefined' && typeof window.fetch === 'function'
    ? window.fetch.bind(window)
    : null;

/** DB/API 무응답 시 화면이 영원히 로딩만 도는 것 방지 */
axios.defaults.timeout = 45000;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);
  const tokenRef = useRef('');
  const tokenTypeRef = useRef('');

  useEffect(() => {
    try {
      if (user && typeof user === 'object') {
        window.__MRT_AUTH_USER__ = user;
      } else {
        delete window.__MRT_AUTH_USER__;
      }
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    tokenRef.current = (user && typeof user === 'object' && user.token) ? String(user.token) : '';
    tokenTypeRef.current = (user && typeof user === 'object' && user.token_type) ? String(user.token_type) : '';
  }, [user]);

  useEffect(() => {
    // 로컬 스토리지에서 사용자 정보 확인
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('사용자 정보 파싱 오류:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    clearTrainingLoginAlertFlags();
    setUser(null);
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // ✅ 토큰 만료(exp) 시 자동 로그아웃
  useEffect(() => {
    // 기존 타이머 정리
    if (logoutTimerRef.current) {
      try { clearTimeout(logoutTimerRef.current); } catch (e) { /* ignore */ }
      logoutTimerRef.current = null;
    }

    const token = (user && typeof user === 'object' && user.token) ? String(user.token) : '';
    if (!token) return undefined;

    // 1) 백엔드 세션 토큰: user.session_expires_at_ms 기준
    const sessExp = (user && typeof user === 'object') ? user.session_expires_at_ms : null;
    const sessExpMs = (sessExp != null && String(sessExp).trim() !== '' && !Number.isNaN(Number(sessExp)))
      ? Number(sessExp)
      : null;

    // 2) 구글 ID 토큰: exp claim 기준
    let expMs = sessExpMs;
    if (!expMs) {
      try {
        const decoded = jwtDecode(token);
        const expSec = decoded?.exp;
        expMs = (typeof expSec === 'number' && Number.isFinite(expSec)) ? (expSec * 1000) : null;
      } catch (e) {
        expMs = null;
      }
    }
    if (!expMs) return undefined;

      const now = Date.now();
      const delay = expMs - now;

      // 이미 만료(혹은 시스템 시계 이슈)면 즉시 로그아웃
      if (delay <= 0) {
        logout();
        return undefined;
      }

      // 만료 시점에 자동 로그아웃
      logoutTimerRef.current = setTimeout(() => {
        logoutTimerRef.current = null;
        logout();
      }, delay);
    // 파싱 실패면 자동 로그아웃 타이머는 걸지 않음(요청 시 API에서 401로 처리)

    return () => {
      if (logoutTimerRef.current) {
        try { clearTimeout(logoutTimerRef.current); } catch (e) { /* ignore */ }
        logoutTimerRef.current = null;
      }
    };
  }, [user]);

  // ✅ 세션 토큰(sliding) 만료 갱신: 응답 헤더 X-Session-Expires-At를 수신하면 user.session_expires_at_ms 갱신
  useEffect(() => {
    if (typeof window === 'undefined' || !MRT_NATIVE_FETCH) return undefined;
    const originalFetch = MRT_NATIVE_FETCH;

    window.fetch = async (input, init) => {
      // ✅ 모든 API 요청에 Authorization 자동 첨부(세션 슬라이딩 연장용)
      let url = '';
      try {
        url = (typeof input === 'string') ? input : String(input?.url || '');
      } catch (e) { url = ''; }

      const tok = String(tokenRef.current || '').trim();
      const apiBase = String(getApiBaseUrl() || '').trim();
      const isApiCall = (() => {
        const u = String(url || '');
        if (!u) return false;
        if (apiBase && u.startsWith(apiBase) && u.includes('/api/')) return true;
        if (u.startsWith('/api/')) return true;
        return false;
      })();

      let nextInit = init || {};
      if (isApiCall && tok) {
        const headers = new Headers((nextInit && nextInit.headers) ? nextInit.headers : undefined);
        if (!headers.get('authorization')) headers.set('Authorization', `Bearer ${tok}`);
        nextInit = { ...nextInit, headers };
      }

      const res = await originalFetch(input, nextInit);
      try {
        const hdr = res?.headers?.get?.('x-session-expires-at') || res?.headers?.get?.('X-Session-Expires-At') || '';
        const exp = String(hdr || '').trim();
        if (exp && /^\d+$/.test(exp)) {
          const nextExpMs = Number(exp);
          setUser((prev) => {
            if (!prev || typeof prev !== 'object') return prev;
            // 세션 토큰일 때만 갱신
            if (String(prev.token_type || '').toLowerCase() !== 'session') return prev;
            const cur = prev.session_expires_at_ms;
            if (cur != null && Number(cur) === nextExpMs) return prev;
            const next = { ...prev, session_expires_at_ms: nextExpMs };
            try { localStorage.setItem('user', JSON.stringify(next)); } catch (e) { /* ignore */ }
            return next;
          });
        }
      } catch (e) { /* ignore */ }
      return res;
    };

    return () => {
      try {
        window.fetch = MRT_NATIVE_FETCH;
      } catch (e) { /* ignore */ }
    };
  }, []);

  // ✅ axios도 동일하게 Authorization 자동 첨부 + 세션 만료 갱신
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      try {
        const tok = String(tokenRef.current || '').trim();
        if (!tok) return config;
        const url = String(config?.url || '');
        const apiBase = String(getApiBaseUrl() || '').trim();
        const isApiCall =
          (apiBase && url.startsWith(apiBase) && url.includes('/api/')) ||
          url.includes('/api/');
        if (!isApiCall) return config;
        config.headers = config.headers || {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${tok}`;
        }
      } catch (e) { /* ignore */ }
      return config;
    });

    const resId = axios.interceptors.response.use((response) => {
      try {
        const exp = String(response?.headers?.['x-session-expires-at'] || '').trim();
        if (exp && /^\d+$/.test(exp)) {
          const nextExpMs = Number(exp);
          setUser((prev) => {
            if (!prev || typeof prev !== 'object') return prev;
            if (String(prev.token_type || '').toLowerCase() !== 'session') return prev;
            const cur = prev.session_expires_at_ms;
            if (cur != null && Number(cur) === nextExpMs) return prev;
            const next = { ...prev, session_expires_at_ms: nextExpMs };
            try { localStorage.setItem('user', JSON.stringify(next)); } catch (e) { /* ignore */ }
            return next;
          });
        }
      } catch (e) { /* ignore */ }
      return response;
    }, (error) => Promise.reject(error));

    return () => {
      try { axios.interceptors.request.eject(reqId); } catch (e) { /* ignore */ }
      try { axios.interceptors.response.eject(resId); } catch (e) { /* ignore */ }
    };
  }, []);

  // 권한 확인 함수
  const hasPermission = (requiredPermission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(requiredPermission);
  };

  const value = {
    user,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth는 AuthProvider 내에서 사용되어야 합니다.');
  }
  return context;
};

export default AuthContext;



